import ApiService from '@shared/services/api.service';

/** Normalize API payload (resource wrapping disabled in API; tolerate camelCase relation name). */
const getFeatureAccess = (data) => {
  if (!data) return null;
  return data.feature_access || data.featureAccess || null;
};

const formatUser = (data) => {
  const fa = getFeatureAccess(data);
  return {
    customerId: data.id,
    ownerId: data.owner_id,
    userName: data.username,
    company: data.company,
    roleBasedAccessControl: data.rbac,
    status: data.status,
    smsBalance: data.sms_balance,
    expiryDate: data.expiry_date,
    createDate: data.create_date,
    lastLogin: data.last_login,
    gold: data.gold_limit,
    silver: data.silver_limit,
    bronze: data.bronze_limit,
    trial: data.trial_limit,
    timezone: data.timezone,
    isEnableContentIntegrityEngine: !!fa && !!fa.enable_content_integrity_engine,
    isEnableImageEngine: !!fa && !!fa.enable_image_engine,
    isEnableNLPEngine: !!fa && !!fa.enable_engine_nlp,
    isEnableAlertTriage: !!fa && !!fa.enable_alert_triage,
    isEnableFqdnAiTriagePrompt: !!fa && !!fa.enable_fqdn_ai_triage_prompt,
    isEnableFqdnAiTriagePreferences: !!fa && !!fa.enable_fqdn_ai_triage_preferences,
    isEnablePageRender: !!fa && !!fa.enable_page_render,
    isEnableStatusCodeMonitoring: !!fa && !!fa.enable_status_code_monitoring,
    isEnableJavaScriptMalwareEngine: !!fa && !!fa.enable_static_malware_engine,
    isEnableDndSslMonitoring: !!fa && !!fa.enable_dns_whois_ssl_monitoring,
    isEnableSMS: !!fa && !!fa.enable_sms,
    isEnableFirecrawl: !!fa && !!fa.enable_firecrawl,
    isEnableVideoChangeEngine: !!fa && !!fa.enable_video_change_detector,
    isEnableChangeEvents: !!fa && !!fa.enable_change_events,
    smsPrefix: fa && fa.sms_number_prefix,
  };
};

const UserManagementService = {
  fetchListUsers: async () => {
    const users = await ApiService.get('/user_management');
    const formatUsers = (users || []).map((item, index) => ({ ...formatUser(item), index: index + 1 }));
    return formatUsers;
  },
  createUser: async (body) => {
    return ApiService.post('/user_management', body);
  },
  detailUserById: async (idUser) => {
    const user = await ApiService.get(`/user_management/${idUser}`);
    return user && formatUser(user);
  },
  updateUserById: async (idUser, body) => {
    return ApiService.put(`/user_management/${idUser}`, body);
  },
  // RBAC API endpoints
  getRbacPermissions: async (customerId) => {
    const response = await ApiService.get(`/customer/${customerId}/rbac`);
    
    // Ensure permissions are properly mapped
    if (response && response.rbac_permissions) {
      // If the API response doesn't have expected field names, map them correctly
      response.rbac_permissions = response.rbac_permissions.map(perm => ({
        custCustomerId: perm.custCustomerId || perm.customer_id,
        custUsername: perm.custUsername || perm.username || perm.customer_username || 'Unknown'
      }));
    }
    
    return response;
  },
  addRbacPermission: async (customerId, targetCustomerId) => {
    return ApiService.post(`/customer/${customerId}/add_rbac`, {
      target_customer_id: targetCustomerId
    });
  },
  removeRbacPermission: async (customerId, targetCustomerId) => {
    // Handle 204 No Content response for successful deletion
    try {
      const response = await ApiService.delete(`/customer/${customerId}/remove_rbac`, {
        data: {
          target_customer_id: targetCustomerId
        }
      });
      return response;
    } catch (error) {
      // Properly handle 404 Not Found errors with consistent format
      if (error.response && error.response.status === 404) {
        throw {
          response: {
            data: {
              message: error.response.data.message || 'RBAC permission does not exist',
              rbac: error.response.data.rbac || []
            }
          }
        };
      }
      throw error;
    }
  },
  batchAddRbacPermissions: async (customerId, targetCustomerIds) => {
    return ApiService.post(`/customer/${customerId}/batch_add_rbac`, {
      target_customer_ids: targetCustomerIds
    });
  },
  batchRemoveRbacPermissions: async (customerId, targetCustomerIds) => {
    // Handle 204 No Content response for successful deletion
    try {
      const response = await ApiService.delete(`/customer/${customerId}/batch_remove_rbac`, {
        data: {
          target_customer_ids: targetCustomerIds
        }
      });
      return response;
    } catch (error) {
      // Handle error responses with proper formatting
      if (error.response) {
        throw {
          status: error.status,
          messages: error.messages,
          response: {
            data: {
              message: error.response.data && error.response.data.message 
                ? error.response.data.message 
                : 'Error removing RBAC permissions',
              rbac: error.response.data && error.response.data.rbac 
                ? error.response.data.rbac 
                : []
            }
          }
        };
      }
      throw error;
    }
  }
};

export default UserManagementService;
